export default function WhyIDontReferStrangers() {
  return (
    <article className="prose">
      <p>
        I understand the job market can be challenging, and referrals are valuable. You might see someone working 
        at your dream company on LinkedIn and wonder if they could help. I want to share my perspective on why 
        I only refer people I've actually worked with, and it's not about being unhelpful—it's about maintaining 
        professional integrity.
      </p>

      <h2>What a Referral Represents</h2>
      <p>
        When I refer someone, I'm putting my professional reputation on the line. I'm essentially telling my 
        employer: "I vouch for this person based on my experience working with them. They're competent, reliable, 
        and will be a good fit for the team." That's a significant professional commitment.
      </p>

      <p>
        If a referral doesn't work out well, it affects not just the candidate but also my credibility. My 
        judgment gets questioned, and my future referrals may carry less weight. This is why I need to have 
        firsthand experience working with someone before I can genuinely vouch for them.
      </p>

      <h2>The Company's Perspective</h2>
      <p>
        Companies value employee referrals because they trust their employees' judgment. When someone refers 
        a candidate, there's an implicit understanding that they know this person's work quality and character. 
        The hiring process becomes more efficient because there's already a level of pre-vetting.
      </p>

      <p>
        When I refer someone I haven't worked with, I can't provide this valuable context. The referral loses 
        its meaning because I can't speak to the candidate's actual abilities or work style. This undermines 
        the trust that makes employee referrals valuable in the first place.
      </p>

      <h2>Why It May Not Help You</h2>
      <p>
        Here's something important to consider: a referral from someone who doesn't know you may not be as 
        helpful as you think. If the hiring manager asks me about you and I have to admit I haven't actually 
        worked with you, it can actually weaken your application rather than strengthen it.
      </p>

      <p>
        You might be better served by applying directly with a strong resume and cover letter that showcases 
        your actual accomplishments. This way, you're being evaluated on your genuine merits and professional 
        track record.
      </p>

      <h2>Building Meaningful Professional Relationships</h2>
      <p>
        I've noticed a trend where referrals are sometimes treated as transactional exchanges rather than 
        professional endorsements based on actual working relationships. This can devalue the referral system 
        that many companies rely on.
      </p>

      <p>
        The most effective professional networks are built on genuine relationships and shared work experiences. 
        These connections carry real weight because they're based on actual collaboration and mutual understanding 
        of each other's capabilities.
      </p>

      <h2>A Better Approach</h2>
      <p>
        If you want referrals, build genuine professional relationships. Work with people. Contribute to open 
        source projects. Attend meetups and conferences. Build a reputation through your work, not through 
        asking strangers for favors.
      </p>

      <p>
        When someone who has actually worked with you refers you, it carries weight. They can speak to specific 
        projects you've worked on together, your problem-solving approach, how you handle pressure, and what 
        it's like to work with you. That's valuable. That's what companies are looking for.
      </p>

      <h2>Moving Forward</h2>
      <p>
        I hope this helps explain my perspective on referrals. It's not about gatekeeping opportunities—it's 
        about maintaining the integrity and value of professional endorsements. A referral should be a meaningful 
        statement based on actual experience working together.
      </p>

      <p>
        I encourage focusing on building your skills, creating a strong portfolio, and forming genuine professional 
        relationships through actual collaboration. These authentic connections often lead to the most valuable 
        career opportunities and mutual support.
      </p>

      <p>
        For those I have worked with and can genuinely vouch for—you already know I'm happy to support you. 
        Those referrals come from a place of confidence in your abilities because I've seen your work firsthand.
      </p>
    </article>
  );
}
